/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { RiMailLine, RiSendPlaneLine, RiDeleteBack2Fill } from 'react-icons/ri';
import { messageAPI } from '../services/api';

const ManageQuote = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replying, setReplying] = useState(false);
  const [replyError, setReplyError] = useState('');
  const [replySuccess, setReplySuccess] = useState('');
  const [filterType, setFilterType] = useState('all'); // all, unread, today

  // Fetch messages on mount
  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await messageAPI.getMessages({ page: 1, per_page: 100 });
      
      if (response.success) {
        // Transform backend data to match component expectations
        const formattedMessages = response.data.messages.map(msg => ({
          ...msg,
          date: new Date(msg.created_at).toLocaleDateString()
        }));
        setMessages(formattedMessages);
      } else {
        setError('Failed to load messages');
      }
    } catch (err) {
      setError(err.error || 'Error loading messages');
      console.error('Error fetching messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await messageAPI.markAsRead(id);
      // Update local state
      setMessages(messages.map(msg => 
        msg.id === id ? { ...msg, read: true } : msg
      ));
      if (selectedMessage?.id === id) {
        setSelectedMessage({ ...selectedMessage, read: true });
      }
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  const deleteMessage = async (id) => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      try {
        await messageAPI.deleteMessage(id);
        setMessages(messages.filter(msg => msg.id !== id));
        if (selectedMessage?.id === id) {
          setSelectedMessage(null);
        }
      } catch (err) {
        console.error('Error deleting message:', err);
        alert('Failed to delete message');
      }
    }
  };

  const sendReply = async () => {
    setReplying(true);
    setReplyError('');
    setReplySuccess('');

    try {
      if (!replyContent.trim()) {
        throw new Error('Reply message cannot be empty');
      }

      const response = await messageAPI.replyToMessage(selectedMessage.id, replyContent);

      if (response.success) {
        setReplySuccess('Reply sent successfully!');
        setShowReplyForm(false);
        setReplyContent('');
        // Update the message with reply status
        setMessages(messages.map(msg =>
          msg.id === selectedMessage.id ? { ...msg, replied: true, reply_message: replyContent } : msg
        ));
        setSelectedMessage({ ...selectedMessage, replied: true, reply_message: replyContent });

        setTimeout(() => setReplySuccess(''), 3000);
      } else {
        throw new Error(response.error || 'Failed to send reply');
      }
    } catch (err) {
      setReplyError(err.message || 'Error sending reply');
      console.error('Error sending reply:', err);
    } finally {
      setReplying(false);
    }
  };

  // Filter messages based on selected filter
  const getFilteredMessages = () => {
    let filtered = messages;
    
    if (filterType === 'unread') {
      filtered = messages.filter(m => !m.read);
    } else if (filterType === 'today') {
      const today = new Date().toLocaleDateString();
      filtered = messages.filter(m => m.date === today);
    }
    
    return filtered;
  };

  return (
    <div className="email-manager">
      <div className="page-header">
        <h2>Email Management</h2>
        <div className="email-stats">
          <span className="stat">
            <RiMailLine /> Total: {messages.length}
          </span>
          <span className="stat">
            Unread: {messages.filter(m => !m.read).length}
          </span>
        </div>
      </div>

      <div className="email-container">
        {/* Message List */}
        <div className="message-list">
          <h3>Incoming Messages</h3>
          
          {error && (
            <div className="alert alert-error">
              <p>{error}</p>
              <button onClick={fetchMessages}>Retry</button>
            </div>
          )}

          {loading ? (
            <div className="loading-state">
              <p>Loading messages...</p>
            </div>
          ) : (
            <>
              <div className="message-filters">
                <button 
                  className={`filter-btn ${filterType === 'all' ? 'active' : ''}`}
                  onClick={() => setFilterType('all')}
                >
                  All
                </button>
                <button 
                  className={`filter-btn ${filterType === 'unread' ? 'active' : ''}`}
                  onClick={() => setFilterType('unread')}
                >
                  Unread
                </button>
                <button 
                  className={`filter-btn ${filterType === 'today' ? 'active' : ''}`}
                  onClick={() => setFilterType('today')}
                >
                  Today
                </button>
              </div>

              <div className="messages">
                {getFilteredMessages().length > 0 ? (
                  getFilteredMessages().map(message => (
                    <div 
                      key={message.id} 
                      className={`message-item ${!message.read ? 'unread' : ''} ${selectedMessage?.id === message.id ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedMessage(message);
                        if (!message.read) {
                          markAsRead(message.id);
                        }
                      }}
                    >
                      <div className="message-header">
                        <h4>{message.name}</h4>
                        <span className="message-date">{message.date}</span>
                      </div>
                      <p className="message-subject">{message.subject}</p>
                      <p className="message-preview">{message.message.substring(0, 100)}...</p>
                      <div className="message-actions">
                        {!message.read && <span className="unread-badge">New</span>}
                        {message.replied && <span className="replied-badge">Replied</span>}
                        <button 
                          className="action-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteMessage(message.id);
                          }}
                        >
                          <RiDeleteBack2Fill />
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-messages">
                    <p>No messages found</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Message Details */}
        <div className="message-details">
          {selectedMessage ? (
            <>
              <div className="message-details-header">
                <div>
                  <h3>{selectedMessage.subject}</h3>
                  <div className="message-info">
                    <span className="sender">{selectedMessage.name} &lt;{selectedMessage.email}&gt;</span>
                    {selectedMessage.phone && <span className="phone">{selectedMessage.phone}</span>}
                    <span className="date">{selectedMessage.date}</span>
                  </div>
                </div>
                <div className="message-actions">
                  <button 
                    className="btn btn-primary"
                    onClick={() => {
                      setShowReplyForm(true);
                      setReplyError('');
                      setReplySuccess('');
                    }}
                  >
                    <RiSendPlaneLine /> Reply
                  </button>
                  <button 
                    className="btn btn-secondary"
                    onClick={() => deleteMessage(selectedMessage.id)}
                  >
                    <RiDeleteBack2Fill /> Delete
                  </button>
                </div>
              </div>

              <div className="message-content">
                <p><strong>Name:</strong> {selectedMessage.name}</p>
                <p><strong>Email:</strong> {selectedMessage.email}</p>
                {selectedMessage.phone && <p><strong>Phone:</strong> {selectedMessage.phone}</p>}
                {selectedMessage.product && <p><strong>Product:</strong> {selectedMessage.product}</p>}
                <hr />
                <p>{selectedMessage.message}</p>
              </div>

              {selectedMessage.reply_message && (
                <div className="reply-display">
                  <h4>Your Reply</h4>
                  <p>{selectedMessage.reply_message}</p>
                </div>
              )}

              {/* Reply Form */}
              {showReplyForm && (
                <div className="reply-form">
                  <h4>Reply to {selectedMessage.name}</h4>
                  
                  {replyError && (
                    <div className="alert alert-error">
                      <p>{replyError}</p>
                    </div>
                  )}

                  {replySuccess && (
                    <div className="alert alert-success">
                      <p>{replySuccess}</p>
                    </div>
                  )}

                  <div className="form-group">
                    <label>Your Reply</label>
                    <textarea
                      value={replyContent}
                      onChange={(e) => setReplyContent(e.target.value)}
                      rows="6"
                      placeholder="Type your reply here..."
                      disabled={replying}
                    />
                  </div>
                  <div className="form-actions">
                    <button 
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowReplyForm(false);
                        setReplyContent('');
                        setReplyError('');
                      }}
                      disabled={replying}
                    >
                      Cancel
                    </button>
                    <button 
                      className="btn btn-primary"
                      onClick={sendReply}
                      disabled={!replyContent.trim() || replying}
                    >
                      <RiSendPlaneLine /> {replying ? 'Sending...' : 'Send Reply'}
                    </button>
                  </div>
                </div>
              )}

              {/* Email Templates */}
              <div className="email-templates">
                <h4>Quick Replies</h4>
                <div className="templates-grid">
                  {[
                    { title: 'Thank You', content: 'Thank you for your inquiry. We appreciate your interest in our pharmaceutical products and services. We will get back to you shortly with more information.' },
                    { title: 'Pricing Info', content: 'Thank you for your interest in pricing. Please note that our pricing varies based on product type, quantity ordered, and delivery location. Our sales team will contact you with a customized quote within 24 hours.' },
                    { title: 'Appointment Confirmation', content: 'Your appointment has been scheduled successfully. Please check your email for the appointment details. If you need to reschedule, please contact us at least 24 hours in advance.' }
                  ].map((template, index) => (
                    <div key={index} className="template-card">
                      <h5>{template.title}</h5>
                      <p>{template.content}</p>
                      <button 
                        className="btn btn-small"
                        onClick={() => {
                          setReplyContent(template.content);
                          setShowReplyForm(true);
                        }}
                      >
                        Use Template
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="no-message-selected">
              <RiMailLine />
              <h3>Select a message to view details</h3>
              <p>Click on any message from the list to read and reply</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageQuote;